import { useState } from "react";
import { FileStack, Loader2, RefreshCw, Download, TrendingDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useOrderProductMatch, useOrderProductLowest } from "./useOrderProducts";
import { packingSummary, priceDelta, trimNum } from "./orderProductFields";
import { OrderProductPicker } from "./OrderProductPicker";

/**
 * 订单产品资料 attachment window — auto-matches the given 产品编号 against
 * 合同订单产品一览表 (L1/L2/L3) and lets the user pull one record in.
 * Reusable: the caller decides what ``onPick(record)`` writes where.
 */
export function OrderProductAttachPanel({ itemNumber, onPick, currentPrice = null }) {
  const { t } = useLanguage();
  const { items, level, loading, reload } = useOrderProductMatch(itemNumber);
  const { lowest, count: pricedCount } = useOrderProductLowest(itemNumber);
  const [pickerOpen, setPickerOpen] = useState(false);

  const pick = (row) => {
    onPick(row);
    toast.success(t("co.imported"));
  };

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-md border border-border/70 bg-secondary/25"
      data-testid="co-attach-panel">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border/60">
        <FileStack size={13} className="text-brand" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {t("co.attachTitle")}
        </span>
        {level && (
          <span className="text-[10px] font-mono text-muted-foreground" data-testid="co-attach-level">
            {t("co.matchedBy")} {level}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5" onClick={reload}
            disabled={loading} data-testid="co-attach-refresh">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          </Button>
          <Button type="button" variant="outline" size="sm"
            className="h-6 px-2 text-[11px] border-brand/40 text-brand hover:bg-brand/5"
            onClick={() => setPickerOpen(true)} data-testid="co-open-import-dialog">
            <Download size={12} /> {t("co.importBtn")}
          </Button>
        </span>
      </div>

      {/* 2026-06 · 历史最低价提示 — 一眼看到最便宜的那笔合同和工厂 */}
      {lowest && (
        <div className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] bg-emerald-50 border-b border-emerald-200/70"
          data-testid="co-lowest-banner">
          <TrendingDown size={12} className="text-emerald-600 shrink-0" />
          <span className="text-emerald-700 shrink-0">{t("co.lowestLabel")}</span>
          <span className="font-mono font-semibold text-emerald-800 shrink-0">
            ¥{trimNum(lowest.purchase_price)}
          </span>
          <span className="truncate min-w-0 text-emerald-700/90">
            {lowest.factory || "-"} · {lowest.contract_no} · {lowest.contract_date}
            {pricedCount > 1 ? ` · ${t("co.lowestOf").replace("{n}", pricedCount)}` : ""}
          </span>
          <Button type="button" size="sm" variant="outline"
            className="h-6 px-2 ml-auto text-[10px] shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            onClick={() => pick(lowest)} data-testid="co-lowest-import">
            {t("co.import")}
          </Button>
        </div>
      )}

      <div className="max-h-[132px] overflow-y-auto divide-y divide-border/50" data-testid="co-attach-list">
        {!loading && items.length === 0 && (
          <p className="px-2.5 py-3 text-[11px] text-muted-foreground" data-testid="co-attach-empty">
            {t("co.noMatch")}
          </p>
        )}
        {items.map((row) => (
          <div key={row.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]"
            data-testid={`co-attach-row-${row.id}`}>
            <span className="font-mono text-muted-foreground w-[70px] shrink-0">{row.contract_no || "-"}</span>
            <span className="truncate flex-1 min-w-0">{row.name_cn || row.name_en || "-"}</span>
            <span className="font-mono shrink-0 tabular-nums" data-testid={`co-attach-price-${row.id}`}>
              {row.purchase_price ? `¥${trimNum(row.purchase_price)}` : "-"}
              <Trend historic={row.purchase_price} current={currentPrice} />
            </span>
            <span className="font-mono text-muted-foreground truncate min-w-0 max-w-[260px] hidden md:inline">
              {packingSummary(row, t)}
            </span>
            <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[10px] shrink-0"
              onClick={() => pick(row)} data-testid={`co-attach-import-${row.id}`}>
              {t("co.import")}
            </Button>
          </div>
        ))}
      </div>

      <OrderProductPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        defaultSearch={itemNumber || ""}
        currentPrice={currentPrice}
        lowestId={lowest?.id || null}
        onPick={(row) => { pick(row); setPickerOpen(false); }}
      />
    </div>
  );
}

/** 与当前采购价对比：红=涨，绿=跌。 */
const Trend = ({ historic, current }) => {
  const d = priceDelta(historic, current);
  if (!d || d.dir === "flat") return null;
  const up = d.dir === "up";
  return (
    <span className={`ml-1 text-[10px] font-semibold ${up ? "text-red-600" : "text-emerald-600"}`}>
      {up ? "▲" : "▼"}{Math.abs(d.pct).toFixed(1)}%
    </span>
  );
};
